import { CardButton } from '@jouzen/ecom-components/v3/CardButton';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { useRef, useState } from 'react';

import Motion from '@/app/components/Motion/index';
import { useScrollPercentage } from '@/app/hooks/useScrollPercentage';

import type { LandingPageProfile } from './LandingPageProfileCard';
import LandingPageProfileCard from './LandingPageProfileCard';

interface LandingPageProfileCardsProps {
  readonly cards: LandingPageProfile[];
}

const LandingPageProfileCards = ({ cards }: LandingPageProfileCardsProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  const { scrollLeftPercentage, scrollRightPercentage } =
    useScrollPercentage(scrollContainerRef);

  return (
    <LayoutGrid data-cy="landing-page-profile-cards">
      <GridItem
        colEnd={{ sm: 'main' }}
        colStart={{ sm: 'main' }}
        className="relative"
      >
        <Motion
          initial={{ opacity: 0 }}
          transition={{ duration: 2.5, type: 'tween', delay: 0.2 }}
          viewport={{ amount: 0.66, once: true }}
          whileInView={{ opacity: 1 }}
        >
          <div
            className="no-scrollbar flex flex-row gap-4 overflow-x-scroll sm:snap-x sm:snap-mandatory lg:snap-none"
            ref={scrollContainerRef}
          >
            {cards.map((card, index) => {
              return (
                <LandingPageProfileCard
                  key={card.name}
                  expanded={index === selectedIndex}
                  onButtonClick={() => {
                    if (selectedIndex === index) {
                      setSelectedIndex(-1);
                    } else {
                      setSelectedIndex(index);
                    }
                  }}
                  {...card}
                />
              );
            })}
          </div>
        </Motion>
      </GridItem>
      <GridItem
        colEnd={{ sm: 'main' }}
        colStart={{ sm: 'main' }}
        className="relative"
      >
        <div className="relative mt-6 h-px bg-sandstone-500">
          <div
            className="absolute inset-y-0 h-full bg-gray-300"
            data-cy="landing-page-profile-cards-scroll-indicator"
            style={{
              left: `${scrollLeftPercentage}%`,
              right: `${scrollRightPercentage}%`,
            }}
          />
        </div>
      </GridItem>
      <GridItem
        colEnd={{ sm: 'main' }}
        colStart={{ sm: 'main' }}
        className="relative"
      >
        <div className="flex flex-row justify-end gap-4 pt-2">
          <CardButton
            className="rotate-180 bg-neutral-600 text-sandstone-200 hover:bg-neutral-500 disabled:bg-neutral-700 disabled:text-neutral-400 disabled:opacity-50"
            color="light"
            data-cy="landing-page-profile-cards-previous-button"
            disabled={selectedIndex < 1}
            onClick={() => {
              setSelectedIndex(selectedIndex - 1);
            }}
            size="xlarge"
            variant="arrow"
          />
          <CardButton
            className="bg-neutral-600 text-sandstone-200 hover:bg-neutral-500 disabled:bg-neutral-700 disabled:text-neutral-400 disabled:opacity-50"
            color="light"
            data-cy="landing-page-profile-cards-next-button"
            disabled={selectedIndex === cards.length - 1}
            onClick={() => {
              setSelectedIndex(selectedIndex + 1);
            }}
            size="xlarge"
            variant="arrow"
          />
        </div>
      </GridItem>
    </LayoutGrid>
  );
};

export default LandingPageProfileCards;
